'use client';

import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { HarborCapability } from '@/constant/harbour';
import { BerthType } from '@/constant';
import {
  useGetAllBookingsQuery,
  useUpdateBookingStatusMutation,
  useCancelBookingMutation,
  useGetAvailableBerthsQuery,
} from '@/services/BookingService';
import { useGetBerthsQuery } from '@/services/BerthManagementService';
import { useGetAddonsQuery } from '@/services/AddonManagementService';
import {
  useGetServiceBookingsQuery,
  useUpdateServiceProviderBookingStatusMutation,
} from '@/services/ServiceProviderBookingService';
import { BookingStatus } from '@/types/booking-management.types';
import type {
  AvailableBerth,
  UnavailabilityItem,
} from '@/types/booking-management.types';
import {
  BerthStatus,
  UnavailabilityType,
  type BerthResponse as BerthDetails,
  type Unavailability as BerthUnavailability,
} from '@/types/berth-management.types';
import type { IAddonData } from '@/types/addon-management.types';
import { bookingConfig } from '@/config';
import EventModal from './components/EventModal';
import DayEventsModal from './components/DayEventsModal';
import {
  type CalendarEvent,
  mapBookingStatus,
  type ViewType,
  type AssetType,
} from './components/types';
import {
  LoadingState,
  NoEventsState,
  NoHarborState,
  WarningMessage,
} from './components/NoDataStates';
import StatusCards from './components/StatusCards';
import FilterSection from './components/FilterSection';
import CalendarView from './components/CalendarView';
import {
  carveAvailableRangesAroundBlocks,
  formatDateDay,
  getOpenSeasonRanges,
  intersectDateRanges,
  shiftDay,
  type DateRange,
} from './components/utils';
import { useNavigate } from 'react-router-dom';

const QUERY_LIMIT = bookingConfig.QUERY_LIMIT;
const POLLING_INTERVAL_MS = bookingConfig.POLL_INTERVAL_MS;

export default function AvailabilityCalendar() {
  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('month');
  const [statusFilters, setStatusFilters] = useState<Set<string>>(
    new Set([
      'confirmed',
      'payment-pending',
      'pending-confirmation',
      'completed',
      'available',
      'blocked',
      'externally-reserved',
    ])
  );
  const [assetTypeFilters, setAssetTypeFilters] = useState<Set<AssetType>>(
    new Set(['berth', 'addon', 'service'])
  );
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [clickedDate, setClickedDate] = useState<Date | undefined>(undefined);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState<{
    open: boolean;
    date?: Date;
    events?: CalendarEvent[];
  }>({ open: false });
  const [userFilter, setUserFilter] = useState<string>('');
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [selectedBerth, setSelectedBerth] = useState<string | null>(null);

  const canAccessReadBookingManagement = hasPermission(
    'berth_booking_management_read'
  );
  const canAccessReadAddonManagement = hasPermission(
    'harbor_addon_management_read'
  );

  const { hasCapability } = usePlanPermissions();
  const canAccessServiceBookings = hasCapability(
    HarborCapability.SERVICES_MANAGE_BOOKINGS
  );

  const { data: bookingsResponse, isLoading: isBookingsLoading } =
    useGetAllBookingsQuery(
      {
        page: 1,
        limit: QUERY_LIMIT,
        harbor_id: harborId || undefined,
        search: userFilter || undefined,
      },
      {
        skip: !harborId,
        pollingInterval: POLLING_INTERVAL_MS,
        refetchOnMountOrArgChange: true,
      }
    );
  const { data: berthsResponse } = useGetBerthsQuery(
    { page: 1, limit: QUERY_LIMIT, harbor_id: harborId || undefined },
    { skip: !harborId }
  );
  const { data: availableBerthsResponse } = useGetAvailableBerthsQuery(
    { harbor_id: harborId || undefined, limit: QUERY_LIMIT },
    {
      skip: !harborId,
      pollingInterval: POLLING_INTERVAL_MS,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: addonsResponse, isLoading: isAddonsLoading } =
    useGetAddonsQuery(
      { harbor_id: harborId || undefined, limit: QUERY_LIMIT },
      {
        skip: !harborId || !canAccessReadAddonManagement,
        pollingInterval: POLLING_INTERVAL_MS,
        refetchOnMountOrArgChange: true,
      }
    );
  const { data: serviceBookingsResponse, isLoading: isServiceBookingsLoading } =
    useGetServiceBookingsQuery(
      {
        page: 1,
        limit: QUERY_LIMIT,
        harbor_id: harborId || undefined,
        search: userFilter || undefined,
      },
      {
        skip: !harborId || !canAccessServiceBookings,
        pollingInterval: POLLING_INTERVAL_MS,
        refetchOnMountOrArgChange: true,
      }
    );

  const totalBerths = useMemo(
    () => berthsResponse?.data?.length || 0,
    [berthsResponse]
  );
  const availableBerthCount = useMemo(
    () => availableBerthsResponse?.data?.length || 0,
    [availableBerthsResponse]
  );
  const availableAddonCount = useMemo(() => {
    if (!addonsResponse?.data) return 0;

    return addonsResponse.data.reduce((count, addonItem) => {
      const addon = addonItem.data || addonItem;
      return addon.is_available ? count + 1 : count;
    }, 0);
  }, [addonsResponse]);
  const [updateBookingStatus, { isLoading: isCancelling }] =
    useUpdateBookingStatusMutation();
  const [updateServiceBookingStatus, { isLoading: isServiceCancelling }] =
    useUpdateServiceProviderBookingStatusMutation();
  const [cancelBooking, { isLoading: isCancellingConfirmed }] =
    useCancelBookingMutation();
  const [cancelReason, setCancelReason] = useState('');

  const backendEvents: CalendarEvent[] = useMemo(() => {
    if (!bookingsResponse) return [];

    const activeBookings =
      bookingsResponse.data?.filter(
        (b) => b.status !== BookingStatus.CANCELLED
      ) ?? [];

    return (
      activeBookings.flatMap((b) => {
        const events: CalendarEvent[] = [];

        if (b.berth) {
          events.push({
            id: b.id,
            title: `${b.berth ? `Berth ${b.berth.berth_number}` : 'Booking'} - ${b.user?.name || b.user?.email}`,
            start: b.start_date.split('T')[0],
            end: b.end_date.split('T')[0],
            status: mapBookingStatus(b.status),
            berthNumber: b.berth?.berth_number || '-',
            user: {
              name: b.user?.name || b.user?.email || 'Guest',
              email: b.user?.email || '',
            },
            isConflicting: b.isConflicting,
            assetType: 'berth',
          });
        }

        if (Array.isArray(b.booking_addons) && b.booking_addons.length > 0) {
          for (const ba of b.booking_addons) {
            const addonId = ba.addon?.id || ba.id;
            const startSrc = ba.start_date ?? b.start_date;
            const endSrc = ba.end_date ?? b.end_date;
            const startDay = startSrc.split('T')[0];
            const endDay = endSrc.split('T')[0];

            const addonForEvent: IAddonData = {
              id: addonId,
              harbor_id: b.harbor?.id || '',
              name: ba.addon?.name || 'Add-on',
              description: undefined,
              price: ba.price_at_booking,
              price_unit: ba.price_unit_at_booking,
              is_available: true,
              quantity: ba.no_of_slots || undefined,
              harbor:
                b.harbor && b.harbor.id && b.harbor.name
                  ? { id: b.harbor.id, name: b.harbor.name }
                  : undefined,
              start_time: startSrc,
              end_time: endSrc,
              is_standalone: false,
            };

            events.push({
              id: `${b.id}`,
              title: `${ba.addon?.name || 'Add-on'} - ${b.user?.name || b.user?.email || 'Guest'}`,
              start: startDay,
              end: endDay,
              status: mapBookingStatus(b.status),
              berthNumber: '-',
              user: {
                name: b.user?.name || b.user?.email || 'Guest',
                email: b.user?.email || '',
              },
              isConflicting: b.isConflicting,
              assetType: 'addon',
              addon: addonForEvent,
              price: ba.price_at_booking,
              priceUnit: ba.price_unit_at_booking,
            });
          }
        }

        // If no berth and no addons
        if (!b.berth && (!b.booking_addons || b.booking_addons.length === 0)) {
          events.push({
            id: b.id,
            title: `Booking - ${b.user?.name || b.user?.email || 'Guest'}`,
            start: b.start_date.split('T')[0],
            end: b.end_date.split('T')[0],
            status: mapBookingStatus(b.status),
            berthNumber: '-',
            user: {
              name: b.user?.name || b.user?.email || 'Guest',
              email: b.user?.email || '',
            },
            isConflicting: b.isConflicting,
            assetType: undefined,
          });
        }

        return events;
      }) ?? []
    );
  }, [bookingsResponse]);

  const serviceEvents: CalendarEvent[] = useMemo(() => {
    const data = serviceBookingsResponse?.data;
    if (!data) return [];

    const activeServiceBookings = data.filter(
      (b) => b.status !== BookingStatus.CANCELLED
    );

    return activeServiceBookings.map((b) => ({
      id: b.id,
      title: `${b.service?.name || 'Service'} - ${b.user?.name || b.user?.email}`,
      start: b.start_date.split('T')[0],
      end: b.end_date.split('T')[0],
      status: mapBookingStatus(b.status),
      berthNumber: '-',
      user: {
        name: b.user?.name || b.user?.email || 'Guest',
        email: b.user?.email || '',
      },
      isConflicting: b.isConflicting,
      assetType: 'service' as const,
      service: b.service
        ? {
            id: b.service.id,
            name: b.service.name,
            description: b.service.description ?? '',
            category: b.service.category ?? '',
            sub_category: b.service.sub_category ?? '',
            service_type: b.service.service_type ?? '',
            language: 'en',
            source_language: 'en',
          }
        : undefined,
      price: b.total_price,
      quantity: b.quantity,
      startTime: b.start_time || undefined,
      endTime: b.end_time || undefined,
    }));
  }, [serviceBookingsResponse]);

  // A SINGLE_BERTH_POOL berth represents a whole pier's pooled capacity through
  // one row, and can carry multiple concurrent confirmed bookings - so instead
  // of a single "Available"/"Booked" chip we compute, per contiguous date
  // range, how many of the pier's units are actually booked at that time (this
  // is in addition to - not a replacement for - the real per-booking chips
  // already rendered from `backendEvents`).
  const bookingRangesByBerthId = useMemo(() => {
    const map = new Map<string, Array<{ start: string; end: string }>>();
    for (const booking of bookingsResponse?.data ?? []) {
      if (booking.status !== BookingStatus.CONFIRMED || !booking.berth?.id) {
        continue;
      }
      const list = map.get(booking.berth.id) ?? [];
      list.push({
        start: booking.start_date.split('T')[0],
        end: booking.end_date.split('T')[0],
      });
      map.set(booking.berth.id, list);
    }
    return map;
  }, [bookingsResponse]);

  const berthAvailableEvents: CalendarEvent[] = useMemo(() => {
    const availableBerths = availableBerthsResponse?.data;
    // SINGLE_BERTH_POOL berths must come from the unfiltered berth list, not
    // the "currently available" list - that endpoint excludes a berth once its
    // confirmed-booking count reaches capacity, which would hide a fully
    // booked pool (and its "X/capacity booked" segments) from the calendar
    // entirely instead of just showing it as fully booked.
    const singleBerthPoolBerths = (berthsResponse?.data ?? []).filter(
      (berth) =>
        berth.pier?.capacity_mode === 'SINGLE_BERTH_POOL' &&
        berth.status === BerthStatus.AVAILABLE
    );
    if (!availableBerths && singleBerthPoolBerths.length === 0) return [];

    const today = new Date();
    const yearStart = formatDateDay(today);
    // Built as a plain string, not `new Date(year, 11, 31)` -> toISOString(),
    // which reinterprets local midnight as UTC and rolls Dec 31 back to Dec 30
    // in timezones ahead of UTC.
    const yearEnd = `${today.getFullYear()}-12-31`;

    // Sweep-line over a SINGLE_BERTH_POOL berth's real booking date ranges so
    // the calendar reflects how the booked/capacity count actually changes
    // as bookings start and end, instead of one static number for the year.
    const computeCapacitySegments = (
      ranges: Array<{ start: string; end: string }>
    ): Array<{ start: string; end: string; booked: number }> => {
      if (ranges.length === 0) {
        return [{ start: yearStart, end: yearEnd, booked: 0 }];
      }

      // Booking end_date is the checkout date - the berth frees up that same
      // day (half-open [start, end) range, matching the backend's overlap
      // checks), so it must NOT be counted as booked or a checkout day would
      // double-count alongside a same-day check-in and inflate the total.
      const points: Array<{ date: string; delta: number }> = [];
      for (const r of ranges) {
        if (r.start > yearEnd || r.end < yearStart) continue;
        points.push({ date: r.start < yearStart ? yearStart : r.start, delta: 1 });
        const endExclusiveDay = r.end > yearEnd ? shiftDay(yearEnd, 1) : r.end;
        points.push({ date: endExclusiveDay, delta: -1 });
      }
      points.sort((a, b) => a.date.localeCompare(b.date));

      const segments: Array<{ start: string; end: string; booked: number }> =
        [];
      let current = yearStart;
      let bookedCount = 0;
      let i = 0;
      while (i < points.length && points[i].date <= yearEnd) {
        const date = points[i].date;
        if (date > current) {
          segments.push({
            start: current,
            end: shiftDay(date, -1),
            booked: bookedCount,
          });
          current = date;
        }
        while (i < points.length && points[i].date === date) {
          bookedCount += points[i].delta;
          i++;
        }
      }
      if (current <= yearEnd) {
        segments.push({ start: current, end: yearEnd, booked: Math.max(0, bookedCount) });
      }
      return segments;
    };

    const enumeratedAvailableBerths = (availableBerths ?? []).filter(
      (berth) => berth.pier?.capacity_mode !== 'SINGLE_BERTH_POOL'
    );
    const berths = [...singleBerthPoolBerths, ...enumeratedAvailableBerths];

    return berths.flatMap((berth) => {
      const unavailabilities = (berth.unavailabilities ||
        []) as UnavailabilityItem[];

      if (berth.pier?.capacity_mode === 'SINGLE_BERTH_POOL') {
        const capacity = berth.pier.pooled_capacity ?? 1;
        const label = berth.pier.boater_label || berth.pier.name;
        const ranges = bookingRangesByBerthId.get(berth.id) ?? [];

        return computeCapacitySegments(ranges).map((segment, index) => ({
          id: `available-${berth.id}-${index}`,
          title: `${label} - ${segment.booked}/${capacity} booked`,
          start: segment.start,
          end: segment.end,
          status: segment.booked >= capacity ? 'blocked' : 'available',
          assetType: 'berth',
          isAvailable: segment.booked < capacity,
          berthNumber: berth.berth_number,
          user: { name: '', email: '' },
          berth: {
            id: berth.id,
            berth_number: berth.berth_number,
          } as unknown as BerthDetails,
        }));
      }

      // A seasonal berth may only be Available inside the harbor's inherited
      // Open season windows - Closed seasons (and any gap not covered by a
      // season entry) must not be shown as Available, even when the berth
      // itself has no explicit unavailability block.
      const seasonalBerth = berth as unknown as AvailableBerth;
      const isSeasonalBerth = seasonalBerth.berth_type === BerthType.SEASONAL;
      const openSeasonRanges =
        isSeasonalBerth &&
        seasonalBerth.harbor?.seasonality_support &&
        seasonalBerth.harbor?.seasonal_dates
          ? getOpenSeasonRanges(
              seasonalBerth.harbor.seasonal_dates,
              yearStart,
              yearEnd,
            )
          : null;
      const boundToOpenSeason = (ranges: DateRange[]): DateRange[] =>
        openSeasonRanges ? intersectDateRanges(ranges, openSeasonRanges) : ranges;

      // If no unavailabilities, berth is available for the full year
      const candidateRanges: DateRange[] =
        unavailabilities.length === 0
          ? [{ start: yearStart, end: yearEnd }]
          : carveAvailableRangesAroundBlocks(
              yearStart,
              yearEnd,
              unavailabilities
                .filter((u) => u.start_date)
                .map((u) => ({
                  start: String(u.start_date).substring(0, 10),
                  end: u.end_date
                    ? String(u.end_date).substring(0, 10)
                    : undefined,
                })),
            );

      // Create events for available ranges
      return boundToOpenSeason(candidateRanges).map((range, index) => ({
        id: `available-${berth.id}-${index}`,
        title: `Berth ${berth.berth_number} (Available)`,
        start: range.start,
        end: range.end,
        status: 'available',
        assetType: 'berth',
        isAvailable: true,
        berthNumber: berth.berth_number,
        user: { name: '', email: '' },
        berth: {
          id: berth.id,
          berth_number: berth.berth_number,
        } as unknown as BerthDetails,
      }));
    });
  }, [availableBerthsResponse, berthsResponse, bookingRangesByBerthId]);

  const berthUnavailabilityEvents: CalendarEvent[] = useMemo(() => {
    const berths = availableBerthsResponse?.data;
    if (!berths) return [];

    const events = berths.flatMap((berth) => {
      const unavailabilities = berth.unavailabilities as UnavailabilityItem[];
      if (!Array.isArray(unavailabilities) || unavailabilities.length === 0) {
        return [];
      }

      return unavailabilities
        .map((range): CalendarEvent | null => {
          if (!range?.start_date) return null;

          const startRaw = String(range.start_date);
          if (startRaw.length < 10) return null;
          const start = startRaw.substring(0, 10);

          let end: string | undefined;
          if (range.end_date) {
            const endRaw = String(range.end_date);
            if (endRaw.length >= 10) end = endRaw.substring(0, 10);
          }

          const rangeId = range.id ?? `${start}-${end ?? ''}`;
          const isExternallyReserved =
            range.type === UnavailabilityType.EXTERNALLY_RESERVED;
          const status: CalendarEvent['status'] = isExternallyReserved
            ? 'externally-reserved'
            : 'blocked';
          const label = isExternallyReserved
            ? 'Reserved outside Saaristo.online'
            : 'Blocked';

          return {
            id: `${isExternallyReserved ? 'externally-reserved' : 'blocked'}-${berth.id}-${rangeId}`,
            title: `Berth ${berth.berth_number} (${label})`,
            start,
            end,
            status,
            assetType: 'berth',
            isBlocked: !isExternallyReserved,
            isExternallyReserved,
            berthNumber: berth.berth_number,
            user: { name: label, email: '' },
            berth: {
              id: berth.id,
              berth_number: berth.berth_number,
            } as unknown as BerthDetails,
            unavailability: {
              ...(range as unknown as BerthUnavailability),
              end_date: range.end_date ?? '',
            } as unknown as BerthUnavailability,
          };
        })
        .filter(Boolean) as CalendarEvent[];
    });

    return events;
  }, [availableBerthsResponse]);

  const availableAddonEvents: CalendarEvent[] = useMemo(() => {
    if (!addonsResponse?.data) return [];

    const today = new Date();
    const yearEnd = formatDateDay(new Date(today.getFullYear(), 11, 31));

    return addonsResponse.data
      .flatMap((addonItem) => {
        const addon = addonItem.data || addonItem;
        if (!addon.is_available) return [];

        if (
          Array.isArray(addon.availability_date_ranges) &&
          addon.availability_date_ranges.length > 0
        ) {
          return addon.availability_date_ranges.map((range, index) => {
            return {
              id: `addon-available-${addon.id}-${index}`,
              title: `${addon.name} (Available)`,
              start: range.start_date.substring(0, 10),
              end: range.end_date ? range.end_date.substring(0, 10) : undefined,
              status: 'available',
              berthNumber: '-',
              user: { name: '', email: '' },
              isAvailable: true,
              assetType: 'addon' as AssetType,
              addon: addon as IAddonData,
              price: addon.price,
              priceUnit: addon.price_unit,
            };
          });
        }

        let startDate = formatDateDay(today);
        let endDate = yearEnd;

        if (addon.start_time) {
          try {
            const startTimestamp = new Date(addon.start_time).getTime();

            if (!isNaN(startTimestamp)) {
              const startObj = new Date(startTimestamp);
              startDate = formatDateDay(startObj);
              endDate = formatDateDay(new Date(startObj.getFullYear(), 11, 31));
            }
          } catch (e) {
            console.error(
              `Invalid date format for addon ${addon.name} (ID: ${addon.id}) start_time:`,
              {
                start_time: addon.start_time,
                error: e,
              }
            );
          }
        }

        return [
          {
            id: `addon-available-${addon.id}`,
            title: `${addon.name} (Available)`,
            start: startDate,
            end: endDate,
            status: 'available',
            berthNumber: '-',
            user: { name: '', email: '' },
            isAvailable: true,
            assetType: 'addon' as AssetType,
            addon: addon as IAddonData,
            price: addon.price,
            priceUnit: addon.price_unit,
          },
        ];
      })
      .filter(Boolean) as CalendarEvent[];
  }, [addonsResponse]);

  const blockedAddonEvents: CalendarEvent[] = useMemo(() => {
    if (!addonsResponse?.data) return [];

    const today = new Date();
    const endOfPeriod = new Date(today.getFullYear(), today.getMonth() + 3, 0);

    return addonsResponse.data
      .map((addonItem) => {
        const addon = addonItem.data || addonItem;
        if (addon.is_available) return null;

        let startDate = formatDateDay(today);
        let endDate = formatDateDay(endOfPeriod);

        if (addon.start_time && addon.end_time) {
          try {
            const startTimestamp = new Date(addon.start_time).getTime();
            const endTimestamp = new Date(addon.end_time).getTime();

            if (!isNaN(startTimestamp) && !isNaN(endTimestamp)) {
              startDate = formatDateDay(new Date(startTimestamp));
              endDate = formatDateDay(new Date(endTimestamp));
            }
          } catch (e) {
            console.error(
              `Invalid date format for addon ${addon.name} (ID: ${addon.id}) time range:`,
              {
                start_time: addon.start_time,
                end_time: addon.end_time,
                error: e,
              }
            );
          }
        }

        return {
          id: `addon-blocked-${addon.id}`,
          title: `${addon.name} (Unavailable)`,
          start: startDate,
          end: endDate,
          status: 'blocked',
          berthNumber: '-',
          user: { name: 'Unavailable', email: '' },
          isBlocked: true,
          assetType: 'addon' as AssetType,
          addon: addon as IAddonData,
          price: addon.price,
          priceUnit: addon.price_unit,
        };
      })
      .filter(Boolean) as CalendarEvent[];
  }, [addonsResponse]);

  const allEvents = useMemo(() => {
    return [
      ...backendEvents,
      ...serviceEvents,
      ...berthAvailableEvents,
      ...berthUnavailabilityEvents,
      ...availableAddonEvents,
      ...blockedAddonEvents,
    ];
  }, [
    backendEvents,
    serviceEvents,
    berthAvailableEvents,
    berthUnavailabilityEvents,
    availableAddonEvents,
    blockedAddonEvents,
  ]);

  const filteredEvents = useMemo(
    () =>
      allEvents.filter((ev) => {
        if (!statusFilters.has(ev.status)) return false;

        if (selectedAssetType) {
          if (ev.assetType !== selectedAssetType) return false;
        } else {
          if (ev.assetType && !assetTypeFilters.has(ev.assetType)) return false;
        }

        if (
          userFilter &&
          !(
            ev.user?.name?.toLowerCase().includes(userFilter.toLowerCase()) ||
            ev.user?.email?.toLowerCase().includes(userFilter.toLowerCase())
          )
        )
          return false;

        // Specific asset selection
        if (selectedBerth) {
          if (
            (selectedAssetType === 'berth' &&
              ev.berthNumber !== selectedBerth) ||
            (selectedAssetType === 'addon' && ev.addon?.id !== selectedBerth)
          )
            return false;
        }

        return true;
      }),
    [
      allEvents,
      statusFilters,
      assetTypeFilters,
      userFilter,
      selectedAssetType,
      selectedBerth,
    ]
  );

  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const toggleAssetTypeFilter = (assetType: AssetType) => {
    setAssetTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(assetType)) next.delete(assetType);
      else next.add(assetType);
      return next;
    });
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: CalendarEvent, date?: Date) => {
    setSelectedEvent(event);
    setClickedDate(date);
    setCancelReason('');
    setIsModalOpen(true);
  };

  const handleCancelBooking = async () => {
    if (!selectedEvent) return;
    try {
      if (selectedEvent.service) {
        await updateServiceBookingStatus({
          id: selectedEvent.id,
          update: {
            status: BookingStatus.CANCELLED,
            cancellation_reason: cancelReason || undefined,
          },
        }).unwrap();
        enqueueSnackbar('Booking cancelled successfully', {
          variant: 'success',
        });
      } else {
        if (selectedEvent.status === 'confirmed') {
          await cancelBooking({
            id: selectedEvent.id,
            data: {
              status: BookingStatus.CANCELLED,
              cancellation_reason: cancelReason || undefined,
            },
          }).unwrap();
        } else if (selectedEvent.status !== 'cancelled') {
          await updateBookingStatus({
            id: selectedEvent.id,
            update: {
              status: BookingStatus.CANCELLED,
              cancellation_reason: cancelReason || undefined,
            },
          }).unwrap();
        }
        enqueueSnackbar('Booking cancelled successfully', {
          variant: 'success',
        });
      }
    } catch (e) {
      console.error('Failed to cancel booking', e);
      enqueueSnackbar('Failed to cancel booking', { variant: 'error' });
    } finally {
      setIsModalOpen(false);
    }
  };

  const getStatusCount = (status: string, useFiltered = false) =>
    (useFiltered ? filteredEvents : allEvents).filter(
      (e) => e.status === status
    ).length;

  const getBerthOptions = () => {
    if (!berthsResponse?.data) return [];

    const berths = berthsResponse.data.map((berth) => ({
      value: berth.berth_number,
      label: `Berth ${berth.berth_number}`,
    }));

    return berths;
  };

  const getAddonName = (addonId: string | null): string => {
    if (!addonId || !addonsResponse?.data) return 'Add-on';

    const addonItem = addonsResponse.data.find((a) => {
      const addon = a.data || a;
      return addon.id === addonId;
    });

    if (!addonItem) return 'Add-on';

    if (addonItem.data && addonItem.data.name) {
      return addonItem.data.name;
    }

    // Type-safe check for name property
    if ('name' in addonItem && typeof addonItem.name === 'string') {
      return addonItem.name;
    }

    return 'Add-on';
  };

  const getAddonOptions = () => {
    if (!addonsResponse?.data) return [];

    const addons = addonsResponse.data.map((addonItem) => {
      const addon = addonItem.data || addonItem;
      return {
        value: addon.id,
        label: addon.name,
      };
    });

    return addons;
  };

  if (!harborId) {
    return <NoHarborState />;
  }

  const handleEditBooking = () => {
    if (!selectedEvent) return;
    if (selectedEvent.service) {
      navigate(`/service-bookings/${selectedEvent.id}/edit`);
    } else {
      navigate(`/booking-management/${selectedEvent.id}/edit`);
    }
  };

  const handleViewBooking = () => {
    if (!selectedEvent) return;
    if (selectedEvent.service) {
      navigate(`/service-bookings/${selectedEvent.id}`);
    } else {
      navigate(`/booking-management/${selectedEvent.id}`);
    }
  };

  return (
    <section
      aria-labelledby="availability-calendar-heading"
      className="min-h-screen"
    >
      <h1 id="availability-calendar-heading" className="sr-only">
        Availability Calendar
      </h1>
      <div className="container mx-auto px-4 py-4">
        {/* Status Cards */}
        <StatusCards
          filteredEvents={filteredEvents}
          backendEvents={[...backendEvents, ...serviceEvents]}
          availableBerthCount={availableBerthCount}
          availableAddonCount={availableAddonCount}
        />

        {/* Filters Section */}
        <FilterSection
          statusFilters={statusFilters}
          toggleStatusFilter={toggleStatusFilter}
          assetTypeFilters={assetTypeFilters}
          toggleAssetTypeFilter={toggleAssetTypeFilter}
          userFilter={userFilter}
          setUserFilter={setUserFilter}
          selectedAssetType={selectedAssetType}
          setSelectedAssetType={setSelectedAssetType}
          selectedBerth={selectedBerth}
          setSelectedBerth={setSelectedBerth}
          resetFilters={() => {
            setSelectedAssetType(null);
            setSelectedBerth(null);
            setAssetTypeFilters(new Set(['berth', 'addon', 'service']));
            setStatusFilters(
              new Set([
                'confirmed',
                'payment-pending',
                'pending-confirmation',
                'completed',
                'available',
                'blocked',
                'externally-reserved',
              ])
            );
            setUserFilter('');
          }}
          getStatusCount={getStatusCount}
          berthOptions={berthsResponse?.data ? getBerthOptions() : []}
          addonOptions={addonsResponse?.data ? getAddonOptions() : []}
          getAddonName={(addonId) => getAddonName(addonId)}
        />

        {/* Loading State */}
        <LoadingState
          isLoading={
            isBookingsLoading || isAddonsLoading || isServiceBookingsLoading
          }
        />

        {/* Warning Message */}
        <WarningMessage totalBerths={totalBerths} />

        {/* Empty State */}
        {filteredEvents.length === 0 &&
          !isBookingsLoading &&
          !isAddonsLoading &&
          !isServiceBookingsLoading && (
            <NoEventsState
              onResetFilters={() => {
                setSelectedAssetType(null);
                setSelectedBerth(null);
                setAssetTypeFilters(new Set(['berth', 'addon', 'service']));
                setStatusFilters(
                  new Set([
                    'confirmed',
                    'payment-pending',
                    'pending-confirmation',
                    'completed',
                    'available',
                    'blocked',
                  ])
                );
                setUserFilter('');
              }}
              isLoading={
                isBookingsLoading || isAddonsLoading || isServiceBookingsLoading
              }
            />
          )}

        {/* Calendar View */}
        {filteredEvents.length > 0 && (
          <CalendarView
            currentDate={currentDate}
            view={view}
            setView={setView}
            filteredEvents={filteredEvents}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            handleToday={handleToday}
            handleEventClick={handleEventClick}
            onMoreClick={(date, events) =>
              setDayEventsModal({ open: true, date, events })
            }
          />
        )}

        {/* Modals */}
        <EventModal
          event={selectedEvent}
          clickedDate={clickedDate}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onCancel={handleCancelBooking}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          isSubmitting={
            isCancelling || isCancellingConfirmed || isServiceCancelling
          }
          onEdit={handleEditBooking}
          onView={handleViewBooking}
          canView={canAccessReadBookingManagement}
        />

        <DayEventsModal
          isOpen={dayEventsModal.open}
          date={dayEventsModal.date}
          events={dayEventsModal.events}
          onClose={() => setDayEventsModal({ open: false })}
          onEventClick={(ev) => {
            setSelectedEvent(ev);
            setClickedDate(dayEventsModal.date);
            setIsModalOpen(true);
            setDayEventsModal({ open: false });
          }}
        />
      </div>
    </section>
  );
}
